import { useEffect } from 'react';

import { useTranslation } from '@/app/providers';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
  selectAccessToken,
  selectBranchCode,
  selectIsAuthenticated,
  selectIsInitialized,
} from '@/features/auth/store/auth-slice';
import { wahaSocketManager } from '@/shared/socket/waha/waha-socket-manager';

/**
 * Bootstraps WAHA raw WebSocket lifecycle from the app shell.
 */
export function useWahaSocketBootstrap(): void {
  const dispatch = useAppDispatch();
  const { locale } = useTranslation();
  const isInitialized = useAppSelector(selectIsInitialized);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const token = useAppSelector(selectAccessToken);
  const branchCode = useAppSelector(selectBranchCode);

  const normalizedLocale = locale.split('-')[0] || 'en';

  useEffect(() => {
    const normalizedToken = token?.trim() ?? '';
    const normalizedBranch = branchCode?.trim() ?? '';
    const canConnect =
      isInitialized && isAuthenticated && Boolean(normalizedToken) && Boolean(normalizedBranch);

    if (!canConnect) {
      wahaSocketManager.stop();
      return;
    }

    wahaSocketManager.start({
      dispatch,
      token: normalizedToken,
      branchCode: normalizedBranch,
      locale: normalizedLocale,
    });
  }, [
    dispatch,
    isInitialized,
    isAuthenticated,
    token,
    branchCode,
    normalizedLocale,
  ]);

  useEffect(() => {
    return () => {
      wahaSocketManager.stop();
    };
  }, []);
}
